import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Button,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Moment } from "moment";
import { getTodayTimings } from "../../utils/commonHelpers";
import {
  API_DATE_TIME_FORMAT,
  DATE_TIME_FORMAT,
  EnumTypes,
  ENV_CONFIG,
} from "../../utils/constants";
import SelectField from "../../components/SelectField";
import { sortBy } from "lodash";
import useStore from "../../store";
import "./style.scss";

const Dashboard = () => {
  const [data, setData] = useState<any>({});
  console.log("data: ", data);

  const [loading, setLoading] = useState(true);
  const { startTime, endTime } = getTodayTimings();
  const [operationsStart, setOperationsStart] = useState<Moment>(startTime);
  const [operationsEnd, setOperationsEnd] = useState<Moment>(endTime);
  const { enums } = useStore();

  const orderSourceOptions = [
    { name: "All sources", id: "all" },
    ...sortBy(enums[EnumTypes.ORDER_SOURCE] || [], ["label"]),
  ];
  const [orderSource, setOrderSource] = useState<string>(
    orderSourceOptions[0]?.id,
  );

  const paymentModeOptions = [
    { label: "All payment modes", value: "all" },
    ...sortBy(enums[EnumTypes.PAYMENT_MODE] || [], ["label"]),
  ];
  const [paymentMode, setPaymentMode] = useState<string>(
    paymentModeOptions?.[0]?.value,
  );

  const fetchAndLoadStats = async () => {
    try {
      let params: { [key: string]: string } = {
        from_date: operationsStart.format(API_DATE_TIME_FORMAT),
        to_date: operationsEnd.format(API_DATE_TIME_FORMAT),
      };
      if (paymentMode !== "all") {
        params["payment_mode"] = paymentMode;
      }
      if (orderSource !== "all") {
        params["source"] = orderSource;
      }

      const url = new URL(
        "/api/superadmins/order-stats/",
        ENV_CONFIG.BACKEND_BASE_URL,
      );

      url.search = new URLSearchParams(params).toString();

      // payment_mode
      const response = await fetch(url);
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Failed to fetch order stats:", error);
    }
  };

  const fetchPreRequisites = async () => {
    setLoading(true);
    await fetchAndLoadStats();
    setLoading(false);
  };

  useEffect(() => {
    fetchPreRequisites();
  }, []);

  return (
    <Box p={2} id="admin5-page-container">
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
        mb={3}
      >
        <DateTimePicker
          className="start-date"
          format={DATE_TIME_FORMAT}
          value={operationsStart}
          onChange={(val) => setOperationsStart(val || startTime)}
        />

        <DateTimePicker
          className="end-date"
          format={DATE_TIME_FORMAT}
          value={operationsEnd}
          onChange={(val) => setOperationsEnd(val || endTime)}
        />

        <SelectField
          value={orderSource}
          options={orderSourceOptions.map((v) => ({
            value: v.id,
            label: v.name,
          }))}
          disabled={false}
          onChange={setOrderSource}
        />

        <SelectField
          value={paymentMode}
          options={paymentModeOptions}
          disabled={false}
          onChange={setPaymentMode}
        />

        <Button variant="contained" onClick={fetchAndLoadStats}>
          Refresh
        </Button>
      </Stack>

      {loading ? (
        <Box textAlign="center">
          <CircularProgress />
        </Box>
      ) : !data ? (
        <Box textAlign="center">Failed to load data.</Box>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Total Orders</Typography>
                  <Typography>{data.total_orders}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Avg Order Value</Typography>
                  <Typography>
                    Rs. {data.average_order_value.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Income</Typography>
                  <Typography>
                    Gross Income:{" "}
                    <Chip
                      size="small"
                      label={Number(data.total_income).toLocaleString()}
                      color="primary"
                    />
                  </Typography>
                  <Typography>
                    Total GST:{" "}
                    <Chip
                      size="small"
                      label={Number(data.total_gst_amount).toLocaleString()}
                      color="error"
                    />
                  </Typography>
                  <Typography>
                    Net Income:{" "}
                    <Chip
                      size="small"
                      label={Number(
                        data.total_income_without_gst,
                      ).toLocaleString()}
                      color="success"
                    />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Items Sold</Typography>
                  <Typography>{data.total_quantity_sold}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Top Categories</Typography>
                  {Object.entries(data.category_summaries)
                    .sort((a: any, b: any) => b[1].income - a[1].income)
                    .slice(0, 3)
                    .map(([cat, val]: any) => (
                      <Typography key={cat}>
                        {cat}: Rs. {val.income}
                      </Typography>
                    ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" gutterBottom>
            Hourly Distribution
          </Typography>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[...data.hourly_distribution]
                    .sort((a, b) => {
                      const ha = a.hour;
                      const hb = b.hour;

                      const indexA = ha >= 12 ? ha - 12 : ha + 12;
                      const indexB = hb >= 12 ? hb - 12 : hb + 12;

                      return indexA - indexB;
                    })
                    .map((obj) => {
                      const { hour: h } = obj;
                      const suffix = h >= 12 ? "PM" : "AM";
                      const displayHour = h % 12 || 12;
                      return { ...obj, hour: `${displayHour} ${suffix}` };
                    })}
                >
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total_income" fill="#3b82f6" name="Income" />
                  <Bar dataKey="order_count" fill="#10b981" name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Typography variant="h5" gutterBottom>
            Category Breakdown
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Income</TableCell>
                  <TableCell>Avg Price</TableCell>
                  <TableCell>Size Breakdown</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(data.category_summaries).map(
                  ([cat, stats]: any) => (
                    <TableRow key={cat}>
                      <TableCell>{cat}</TableCell>
                      <TableCell>{stats.quantity_sold}</TableCell>
                      <TableCell>Rs. {stats.income}</TableCell>
                      <TableCell>
                        Rs. {stats.average_selling_price.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {Object.entries(stats.size_breakdown).map(
                          ([size, s]: any) => (
                            <Typography key={size} variant="caption">
                              {size}: {s.quantity_sold} (Rs. {s.income})<br />
                            </Typography>
                          ),
                        )}
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h5" gutterBottom>
            Item Sales Detail
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Income</TableCell>
                  <TableCell>Avg Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items_summaries.map((item: any) => (
                  <TableRow key={item.item}>
                    <TableCell>
                      <Chip label={item.category} size="small" />
                    </TableCell>
                    <TableCell>
                      {item.name} ({item.size})
                    </TableCell>
                    <TableCell>{item.quantity_sold}</TableCell>
                    <TableCell>Rs. {item.income}</TableCell>
                    <TableCell>
                      Rs. {item.average_selling_price.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
